import React from 'react';
import { Box, Typography, Container, List, ListItem, ListItemText } from '@mui/material';
import { useSelector } from 'react-redux';

const PrivacyPolicy = () => {
  const { language } = useSelector((state) => state.auth);

  return (
    <>
      {language === 'FRENCH' ? (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Politique de confidentialité – JMC Béton
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Dernière mise à jour : 21 avril 2025
          </Typography>

          <Box my={3}>
            <Typography variant="h6" fontWeight="bold">
              1. Introduction
            </Typography>
            <Typography paragraph>
              L’application mobile JMC Béton a été développée par Infinity Group Solutions pour le compte de Johnny
              Matala Company («JMC»), propriétaire exclusif de l’application. Celle-ci est destinée à faciliter les
              commandes de béton en République démocratique du Congo (RDC). La présente politique explique quelles
              données personnelles sont collectées, comment elles sont utilisées, et les droits reconnus aux
              utilisateurs selon le Code numérique n°23‑010 du 13 mars 2023 ainsi que les principes généraux de
              protection de la vie privée en vigueur en RDC.
            </Typography>
          </Box>

          <Box my={3}>
            <Typography variant="h6" fontWeight="bold">
              2. Données collectées
            </Typography>
            <Typography paragraph>Nous pouvons recueillir :</Typography>
            <List sx={{ pl: 2 }}>
              <ListItem>
                <Typography>
                  • Données fournies par l’utilisateur : nom, numéro de téléphone, détails de la commande (volume,
                  dosage, date et créneau de livraison) et tout commentaire libre.
                </Typography>
              </ListItem>
              <ListItem>
                <Typography>
                  • Données techniques : identifiant de l’appareil, version Android, langue, journaux d’erreurs et
                  historique de notifications.
                </Typography>
              </ListItem>
            </List>
            <Typography paragraph>
              Aucune information de paiement n’est collectée ni stockée dans l’application.
            </Typography>
          </Box>

          <Box my={3}>
            <Typography variant="h6" fontWeight="bold">
              3. Finalités d’utilisation
            </Typography>
            <Typography paragraph>Les données servent exclusivement à :</Typography>
            <List sx={{ pl: 2 }}>
              <ListItem>
                <Typography>• Enregistrer et gérer vos commandes de béton ;</Typography>
              </ListItem>
              <ListItem>
                <Typography>• Vous notifier de l’avancement (confirmation, préparation, livraison) ;</Typography>
              </ListItem>
              <ListItem>
                <Typography>• Répondre à vos demandes via le service client ;</Typography>
              </ListItem>
              <ListItem>
                <Typography>
                  • Améliorer la fiabilité et la sécurité de l’application grâce à des statistiques anonymisées.
                </Typography>
              </ListItem>
            </List>
          </Box>

          <Box my={3}>
            <Typography variant="h6" fontWeight="bold">
              4. Partage des données
            </Typography>
            <Typography paragraph>
              Vos données personnelles ne sont jamais vendues. Elles peuvent être partagées uniquement :
            </Typography>
            <List sx={{ pl: 2 }}>
              <ListItem>
                <Typography>• Avec le personnel de JMC chargé de la logistique et du support ;</Typography>
              </ListItem>
              <ListItem>
                <Typography>
                  • Avec des fournisseurs techniques (hébergement, maintenance) liés par des accords de confidentialité
                  ;
                </Typography>
              </ListItem>
              <ListItem>
                <Typography>• Lorsque la loi congolaise l’exige ou en vertu d’une décision judiciaire.</Typography>
              </ListItem>
            </List>
          </Box>

          <Box my={3}>
            <Typography variant="h6" fontWeight="bold">
              5. Stockage et sécurité
            </Typography>
            <Typography paragraph>
              Les informations sont hébergées sur des serveurs sécurisés, protégés par chiffrement, pare‑feu et contrôle
              d’accès par rôle. Nous mettons régulièrement à jour nos mesures de sécurité conformément aux bonnes
              pratiques locales et internationales.
            </Typography>
          </Box>

          <Box my={3}>
            <Typography variant="h6" fontWeight="bold">
              6. Durée de conservation
            </Typography>
            <List sx={{ pl: 2 }}>
              <ListItem>
                <Typography>
                  • Données de commande : conservées dix (10) ans afin de répondre aux obligations comptables et
                  d’archivage.
                </Typography>
              </ListItem>
              <ListItem>
                <Typography>• Journaux techniques : conservés douze (12) mois puis anonymisés.</Typography>
              </ListItem>
            </List>
          </Box>

          <Box my={3}>
            <Typography variant="h6" fontWeight="bold">
              7. Vos droits
            </Typography>
            <Typography paragraph>
              Conformément au Code numérique 2023 et aux principes constitutionnels relatifs à la vie privée, vous
              disposez des droits d’accès, de rectification, d’effacement et d’opposition au traitement de vos données.
              Pour exercer l’un de ces droits, contactez‑nous selon les modalités ci‑dessous. Une réponse motivée vous
              sera fournie dans un délai maximum de trente (30) jours.
            </Typography>
          </Box>

          <Box my={3}>
            <Typography variant="h6" fontWeight="bold">
              8. Modifications de la politique
            </Typography>
            <Typography paragraph>
              Toute modification substantielle sera annoncée dans l’application ou par notification push. La version la
              plus récente est toujours disponible depuis le menu « Paramètres » de l’application.
            </Typography>
          </Box>

          <Box my={3}>
            <Typography variant="h6" fontWeight="bold">
              9. Contact
            </Typography>
            <Typography>
              Johnny Matala Company – Service Protection des données
              <br />
              615b, 11ᵉ Rue, C/Limete, Q/Résidentiel PB, Kinshasa, RDC
              <br />
              +243 848 013 975
            </Typography>
          </Box>
        </Container>
      ) : (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Privacy Policy – JMC Béton
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Last updated: 21 April 2025
          </Typography>

          <Box mt={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              1. Introduction
            </Typography>
            <Typography>
              The JMC Béton mobile application was developed by Infinity Group Solutions on behalf of Johnny Matala
              Company (“JMC”), the exclusive owner of the application. It is designed to simplify concrete ordering in
              the Democratic Republic of Congo (DRC). This policy explains what personal data is collected, how it is
              used, and the rights granted to users under the Digital Code No.23-010 of March 13, 2023, as well as
              general privacy principles applicable in the DRC.
            </Typography>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              2. Data We Collect
            </Typography>
            <Typography fontWeight="bold">We may collect:</Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="User‑provided data:"
                  secondary="name, phone number, order details (volume, mix, requested delivery date and slot) and any free‑text comments."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Technical data:"
                  secondary="device identifier, Android version, language settings, crash logs and notification history."
                />
              </ListItem>
            </List>
            <Typography>No payment information is collected or stored in the app.</Typography>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              3. Purpose of Processing
            </Typography>
            <Typography>We process your data solely to:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="Register and manage your concrete orders;" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Send status notifications (confirmation, preparation, delivery);" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Provide customer support;" />
              </ListItem>
              <ListItem>
                <ListItemText primary="Improve app reliability and security through anonymised diagnostics." />
              </ListItem>
            </List>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              4. Data Sharing
            </Typography>
            <Typography>Personal data are never sold. They may be shared only:</Typography>
            <List>
              <ListItem>
                <ListItemText primary="With JMC staff responsible for logistics and support;" />
              </ListItem>
              <ListItem>
                <ListItemText primary="With technical providers (hosting, maintenance) bound by confidentiality agreements;" />
              </ListItem>
              <ListItem>
                <ListItemText primary="When required by DRC law or pursuant to a court order." />
              </ListItem>
            </List>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              5. Storage and Security
            </Typography>
            <Typography>
              Data are stored on secure servers, protected by encryption, firewalls and role‑based access controls.
              Security measures are reviewed and updated regularly in line with local and international best practices.
            </Typography>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              6. Retention Period
            </Typography>
            <List>
              <ListItem>
                <ListItemText
                  primary="Order data:"
                  secondary="retained for ten (10) years to meet accounting and archiving obligations."
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="Technical logs:"
                  secondary="retained for twelve (12) months and then anonymised."
                />
              </ListItem>
            </List>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              7. Your Rights
            </Typography>
            <Typography>
              Under the 2023 Digital Code and constitutional privacy provisions, you have the right to access, correct,
              delete or object to the processing of your personal data. To exercise any of these rights, please contact
              us as indicated below. We will provide a reasoned response within thirty (30) days.
            </Typography>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              8. Changes to This Policy
            </Typography>
            <Typography>
              Any material change will be announced in‑app or via push notification. The latest version is always
              available from the “Settings” menu in the app.
            </Typography>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              9. Contact
            </Typography>
            <Typography>
              <strong>Johnny Matala Company – Data Protection Office</strong>
              <br />
              615b, 11th Street, Limete, Residential PB, Kinshasa, DRC
              <br />
              +243 848 013 975
            </Typography>
          </Box>
        </Container>
      )}
    </>
  );
};

export default PrivacyPolicy;
